import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Upload, Trash2, LogOut, ExternalLink, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchLookbook, uploadLookbook, deleteLookbook, getToken, logout } from "@/lib/adminApi";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminLookbook() {
  const [shots, setShots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const hasToken = !!getToken();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetchLookbook();
      setShots(r.items || []);
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("Lookbook load failed:", err);
      toast.error("Erreur de chargement");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (hasToken) load();
  }, [hasToken, load]);

  if (!hasToken) return <Navigate to="/admin/login" replace />;

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    let success = 0;
    for (const f of files) {
      try {
        await uploadLookbook(f);
        success += 1;
      } catch (err) {
        toast.error(`${f.name} : ${err.response?.data?.detail || "échec"}`);
      }
    }
    if (success) toast.success(`${success} photo(s) ajoutée(s)`);
    setUploading(false);
    e.target.value = "";
    load();
  };

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette photo du lookbook ?")) return;
    try {
      await deleteLookbook(id);
      toast.success("Photo supprimée");
      load();
    } catch {
      toast.error("Erreur");
    }
  };

  const resolveUrl = (u) => (u && u.startsWith("/api/") ? `${BACKEND_URL}${u}` : u);

  const renderGrid = () => {
    if (loading) {
      return <p className="text-white/40 text-center py-12">···</p>;
    }
    if (shots.length === 0) {
      return (
        <div className="rounded-3xl border border-dashed border-yellow-500/30 bg-yellow-500/[0.03] py-16 px-6 text-center">
          <ImageIcon className="w-12 h-12 mx-auto text-yellow-400/60 mb-4" strokeWidth={1.4} />
          <p className="text-yellow-200 text-lg font-bold mb-1">Aucune photo pour le moment</p>
          <p className="text-white/55 text-sm">Clique sur « Ajouter des photos » pour publier ton premier lookbook.</p>
        </div>
      );
    }
    return (
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="admin-lookbook-grid">
        {shots.map((s) => (
          <div
            key={s.id}
            data-testid={`admin-lookbook-shot-${s.id}`}
            className="relative rounded-2xl overflow-hidden border border-yellow-500/15 aspect-square group"
          >
            <img src={resolveUrl(s.image_url)} alt="" className="w-full h-full object-cover" />
            <button
              onClick={() => onDelete(s.id)}
              data-testid={`admin-lookbook-delete-${s.id}`}
              className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-red-300 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/30"
              title="Supprimer"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="admin-lookbook-page">
      {/* Top bar */}
      <header
        className="sticky top-0 z-30 border-b border-yellow-600/20 backdrop-blur-md"
        style={{ background: "rgba(5,5,5,0.85)" }}
      >
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/logos/braf_zak_logo_main.jpeg" alt="" className="w-10 h-10 rounded-full object-cover border border-yellow-500/40" />
            <div>
              <p className="text-[10px] tracking-[0.3em] text-yellow-400/70">ADMIN</p>
              <p className="text-lg font-black" style={{ fontFamily: '"Bebas Neue", system-ui', color: "#F4D77A", letterSpacing: "0.12em" }}>
                BRAF&nbsp;ZAK
              </p>
            </div>
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/admin" className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full">
              Catalogue
            </Link>
            <Link to="/admin/orders" className="text-xs uppercase tracking-widest text-yellow-200/70 hover:text-yellow-300 px-3 py-2 rounded-full">
              Commandes
            </Link>
            <Link
              to="/admin/lookbook"
              data-testid="admin-nav-lookbook"
              className="text-xs uppercase tracking-widest text-black font-bold px-3 py-2 rounded-full"
              style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
            >
              Lookbook
            </Link>
            <Link
              to="/"
              target="_blank"
              className="text-xs uppercase tracking-widest text-yellow-300 hover:text-yellow-200 px-3 py-2 rounded-full border border-yellow-500/30 inline-flex items-center gap-2"
            >
              <ExternalLink className="w-3.5 h-3.5" /> Site
            </Link>
            <button
              onClick={logout}
              className="text-xs uppercase tracking-widest text-white/70 hover:text-red-300 px-3 py-2 rounded-full border border-white/20 inline-flex items-center gap-2"
            >
              <LogOut className="w-3.5 h-3.5" /> Déco
            </button>
          </nav>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white" style={{ fontFamily: '"Bebas Neue", system-ui' }}>
              Lookbook
            </h1>
            <p className="text-xs text-yellow-300/70 tracking-widest uppercase mt-1">
              {shots.length} photo(s) publiée(s)
            </p>
            <p className="text-[12px] text-white/60 mt-2 max-w-2xl">
              Uploade tes plus belles photos (boutique, équipes sponsorisées, séances photo…). Elles s'afficheront automatiquement sur la page Lookbook publique.
            </p>
          </div>
          <label
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-black font-bold cursor-pointer ${uploading ? "opacity-50 pointer-events-none" : ""}`}
            style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
          >
            <Upload className="w-4 h-4" />
            {uploading ? "Upload en cours…" : "Ajouter des photos"}
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={onUpload}
              disabled={uploading}
              data-testid="admin-lookbook-upload"
              className="hidden"
            />
          </label>
        </div>

        {renderGrid()}
      </main>
    </div>
  );
}
